import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Controller, FormProvider, useForm, useFormContext, useWatch, type FieldValues } from "react-hook-form";
import { AlertTriangle, ArrowLeft, ArrowRight, Check, ChevronDown, ChevronUp, Pencil, Send } from "lucide-react";
import { Button, Checkbox, Field, Stepper, cn } from "@ncp/ui";
import { evaluate, visibility, visiblePathsInStep, visibleSteps } from "./conditions";
import { defaultsFor, descriptionOf, getAtPath, labelOf, localize, schemaAtPath } from "./schema";
import type { FormContext, FormDocument, FormIssue, FormLocale, PropertySchema, UiField, UiSection, UiStep } from "./types";
import { formResolver, validateSubmission } from "./validation";
import { ensureBuiltInWidgets } from "./widgets";
import { getWidget, resolveWidgetKey, widgetOptions, type WidgetProps } from "./widgets/registry";
import { ReadOnlyValue } from "./FormReadOnly";

const T = {
  next: { en: "Continue", ar: "متابعة" },
  back: { en: "Back", ar: "رجوع" },
  submit: { en: "Submit", ar: "إرسال" },
  save: { en: "Save draft", ar: "حفظ مسودة" },
  fixErrors: { en: (n: number) => `${n} field${n === 1 ? "" : "s"} need attention.`, ar: (n: number) => `${n} حقل يحتاج إلى مراجعة.` },
  edit: { en: "Edit", ar: "تعديل" },
  declaration: { en: "I confirm that the information provided is complete and accurate.", ar: "أؤكد أن المعلومات المقدمة كاملة وصحيحة." },
  stepOf: { en: (a: number, b: number) => `Step ${a} of ${b}`, ar: (a: number, b: number) => `الخطوة ${a} من ${b}` },
  warnings: { en: "Please review", ar: "يرجى المراجعة" },
};

export type FormMode = "wizard" | "single" | "readonly";

export interface FormRendererProps<T extends FieldValues = FieldValues> {
  document: FormDocument;
  /** Initial values; merged over schema defaults. */
  value?: Partial<T>;
  onChange?: (values: T) => void;
  onSubmit?: (values: T) => void | Promise<void>;
  onSaveDraft?: (values: T) => void | Promise<void>;
  mode?: FormMode;
  locale?: FormLocale;
  context?: Partial<FormContext>;
  /** Controlled step index (wizard). */
  step?: number;
  onStepChange?: (index: number) => void;
  /** Extra actions rendered next to the navigation buttons. */
  actions?: ReactNode;
  submitting?: boolean;
  /** Renders in builder preview: no stepper animations, all steps reachable. */
  preview?: boolean;
  className?: string;
  /** Called when a field value changes with its path (for live summaries / provenance). */
  onFieldChange?: (path: string, value: unknown) => void;
  /** Hides the wizard's own submit button (host renders its own). */
  hideSubmit?: boolean;
}

interface RendererCtx {
  document: FormDocument;
  locale: FormLocale;
  context: FormContext;
  readOnly: boolean;
  onFieldChange?: (path: string, value: unknown) => void;
}

/** Renders a FormDocument: wizard steps, sections, conditional fields and validation. Used by the portal, the back office and the builder preview. */
export function FormRenderer<T extends FieldValues = FieldValues>({ document, value, onChange, onSubmit, onSaveDraft, mode = "wizard", locale = "en", context, step: controlledStep, onStepChange, actions, submitting, preview, className, onFieldChange, hideSubmit }: FormRendererProps<T>) {
  ensureBuiltInWidgets();
  const dir = locale === "ar" ? "rtl" : "ltr";
  const ctx = useMemo<FormContext>(() => ({ locale, dir, ...context }), [locale, dir, context]);
  const defaults = useMemo(() => ({ ...(defaultsFor(document.schema) as object), ...(value ?? {}) }) as T, [document.schema, value]);
  const resolver = useMemo(() => formResolver<T>(document.schema, document.uiSchema, document.rules, locale), [document.schema, document.uiSchema, document.rules, locale]);
  const form = useForm<T>({ defaultValues: defaults as never, resolver, mode: "onTouched", shouldUnregister: false });
  const [internalStep, setInternalStep] = useState(0);
  const step = controlledStep ?? internalStep;
  const setStep = useCallback((i: number) => { setInternalStep(i); onStepChange?.(i); }, [onStepChange]);
  const values = useWatch({ control: form.control }) as T;
  const lastEmitted = useRef<string>("");
  useEffect(() => {
    if (!onChange) return;
    const json = JSON.stringify(values);
    if (json !== lastEmitted.current) {
      lastEmitted.current = json;
      onChange(values);
    }
  }, [values, onChange]);
  // reset when the document itself changes (builder edits)
  const docRef = useRef(document);
  useEffect(() => {
    if (docRef.current !== document) {
      docRef.current = document;
      form.reset({ ...(defaultsFor(document.schema) as object), ...(form.getValues() as object) } as never, { keepDirty: true, keepErrors: false });
    }
  }, [document, form]);

  const steps = useMemo(() => visibleSteps(document.uiSchema, values), [document.uiSchema, values]);
  const current = steps[Math.min(step, Math.max(steps.length - 1, 0))];
  const readOnly = mode === "readonly";
  const rctx: RendererCtx = { document, locale, context: ctx, readOnly, onFieldChange };
  const [submitIssues, setSubmitIssues] = useState<FormIssue[]>([]);

  const validateStep = async (s: UiStep) => {
    const paths = visiblePathsInStep(s, form.getValues());
    const ok = await form.trigger(paths as never, { shouldFocus: true });
    return ok;
  };
  const next = async () => {
    if (!current) return;
    if (!preview && !(await validateStep(current))) return;
    setStep(Math.min(step + 1, steps.length - 1));
  };
  const back = () => setStep(Math.max(step - 1, 0));
  const submit = form.handleSubmit(
    async (v) => {
      setSubmitIssues([]);
      const { __declaration: _declaration, ...clean } = v as Record<string, unknown>;
      void _declaration;
      await onSubmit?.(clean as T);
    },
    () => {
      const issues = validateSubmission(document.schema, document.uiSchema, document.rules, form.getValues(), { locale });
      setSubmitIssues(issues);
      // jump to the first step with an error
      const idx = steps.findIndex((s) => visiblePathsInStep(s, form.getValues()).some((p) => issues.some((i) => i.path === p || i.path.startsWith(p + "."))));
      if (idx >= 0) setStep(idx);
    },
  );
  const warnings = useMemo(() => validateSubmission(document.schema, document.uiSchema, document.rules, values, { locale }).filter((i) => i.severity === "warning"), [document, values, locale]);

  return (
    <FormProvider {...form}>
      <form dir={dir} className={cn("space-y-6", locale === "ar" && "font-arabic", className)} onSubmit={submit} noValidate>
        {mode === "wizard" && steps.length > 1 && document.uiSchema.layout?.showStepper !== false ? (
          <Stepper steps={steps.map((s) => ({ key: s.key, label: localize(s.title, locale), description: s.description ? localize(s.description, locale) : undefined }))} current={Math.min(step, steps.length - 1)} onStepClick={(i) => (preview || i < step ? setStep(i) : undefined)} />
        ) : null}
        {submitIssues.filter((i) => i.severity === "error").length > 0 ? (
          <div role="alert" className="flex items-start gap-2 rounded-xl border border-danger-100 bg-danger-50 p-3 text-sm text-danger-700">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <div>
              <p className="font-medium">{T.fixErrors[locale](submitIssues.filter((i) => i.severity === "error").length)}</p>
              <ul className="mt-1 list-inside list-disc text-xs">{submitIssues.filter((i) => i.severity === "error").slice(0, 6).map((i, k) => <li key={k}>{i.message}</li>)}</ul>
            </div>
          </div>
        ) : null}
        {mode === "wizard" ? (
          current ? <StepView key={current.key} step={current} ctx={rctx} index={step} total={steps.length} allSteps={steps} onEdit={(i) => setStep(i)} /> : null
        ) : (
          steps.map((s, i) => <StepView key={s.key} step={s} ctx={rctx} index={i} total={steps.length} allSteps={steps} flat onEdit={() => undefined} />)
        )}
        {warnings.length > 0 ? (
          <div className="rounded-xl border border-warning-100 bg-warning-50 p-3 text-sm text-warning-700">
            <p className="font-medium">{localize(T.warnings, locale)}</p>
            <ul className="mt-1 list-inside list-disc text-xs">{warnings.map((w, k) => <li key={k}>{w.message}</li>)}</ul>
          </div>
        ) : null}
        {!readOnly ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-sand-200 pt-4">
            <div className="flex items-center gap-2">
              {mode === "wizard" && step > 0 ? <Button type="button" variant="outline" onClick={back}>{dir === "rtl" ? <ArrowRight /> : <ArrowLeft />} {localize(T.back, locale)}</Button> : null}
              {onSaveDraft ? <Button type="button" variant="ghost" onClick={() => onSaveDraft(form.getValues())}>{localize(T.save, locale)}</Button> : null}
              {actions}
            </div>
            <div className="flex items-center gap-2">
              {mode === "wizard" && steps.length > 1 ? <span className="text-xs text-muted-foreground">{T.stepOf[locale](Math.min(step, steps.length - 1) + 1, steps.length)}</span> : null}
              {mode === "wizard" && step < steps.length - 1 ? (
                <Button type="button" variant="dark" onClick={next}>{localize(T.next, locale)} {dir === "rtl" ? <ArrowLeft /> : <ArrowRight />}</Button>
              ) : !hideSubmit ? (
                <Button type="submit" variant="gold" loading={submitting}><Send /> {localize(document.uiSchema.submitLabel ?? T.submit, locale)}</Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </form>
    </FormProvider>
  );
}

function StepView({ step, ctx, index, total, allSteps, flat, onEdit }: { step: UiStep; ctx: RendererCtx; index: number; total: number; allSteps: UiStep[]; flat?: boolean; onEdit: (i: number) => void }) {
  const { locale } = ctx;
  const values = useFormContext().watch();
  if (step.review) return <ReviewStep step={step} ctx={ctx} steps={allSteps.filter((s) => !s.review)} onEdit={onEdit} />;
  return (
    <section className={cn("space-y-5", flat && index < total - 1 && "border-b border-sand-200 pb-6")} aria-labelledby={`step-${step.key}`}>
      <div>
        <h2 id={`step-${step.key}`} className="text-xl font-semibold">{localize(step.title, locale)}</h2>
        {step.description ? <p className="text-sm text-muted-foreground">{localize(step.description, locale)}</p> : null}
      </div>
      {step.sections.filter((s) => evaluate(s.visibleWhen, values)).map((section) => <SectionView key={section.key} section={section} ctx={ctx} />)}
    </section>
  );
}

function SectionView({ section, ctx }: { section: UiSection; ctx: RendererCtx }) {
  const { locale } = ctx;
  const [collapsed, setCollapsed] = useState(section.collapsed === true);
  const cols = section.columns ?? 2;
  const values = useFormContext().watch();
  const title = section.title ? localize(section.title, locale) : "";
  return (
    <div className={cn(title && "rounded-2xl border border-sand-200 bg-white/60 p-4 sm:p-5")}>
      {title ? (
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold">{title}</h3>
            {section.description ? <p className="text-sm text-muted-foreground">{localize(section.description, locale)}</p> : null}
          </div>
          {section.collapsible ? <Button type="button" size="icon-sm" variant="ghost" onClick={() => setCollapsed((c) => !c)} aria-expanded={!collapsed}>{collapsed ? <ChevronDown /> : <ChevronUp />}</Button> : null}
        </div>
      ) : null}
      {!collapsed ? (
        <div className={cn("grid gap-4", cols === 1 && "grid-cols-1", cols === 2 && "grid-cols-1 sm:grid-cols-2", cols === 3 && "grid-cols-1 sm:grid-cols-3", cols === 4 && "grid-cols-2 sm:grid-cols-4")}>
          {section.fields.filter((f) => evaluate(f.visibleWhen, values)).map((f, i) => (
            <div key={`${f.path}-${i}`} className={spanClass(f, cols, ctx)} style={spanStyle(f, cols, ctx)}>
              {f.content ? <ContentBlock text={localize(f.content, locale)} /> : <FieldView field={f} ctx={ctx} />}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function effectiveSpan(f: UiField, cols: number, ctx: RendererCtx): number {
  if (f.span) return f.span;
  const node = schemaAtPath(ctx.document.schema, f.path);
  const widget = node ? getWidget(resolveWidgetKey(node, f)) : undefined;
  const xs = node?.["x-ncp"]?.span;
  if (xs) return xs;
  if (widget?.defaultSpan) return widget.defaultSpan;
  if (f.content) return 12;
  return Math.round(12 / cols);
}

function spanClass(f: UiField, cols: number, ctx: RendererCtx): string {
  const span = effectiveSpan(f, cols, ctx);
  if (span >= 12 || cols === 1) return "col-span-full";
  return "";
}

function spanStyle(f: UiField, cols: number, ctx: RendererCtx): React.CSSProperties | undefined {
  const span = effectiveSpan(f, cols, ctx);
  if (span >= 12 || cols === 1) return undefined;
  const units = Math.max(1, Math.min(cols, Math.round((span / 12) * cols)));
  return units > 1 ? { gridColumn: `span ${units} / span ${units}` } : undefined;
}

function ContentBlock({ text }: { text: string }) {
  // lightweight markdown: paragraphs, **bold**, bullet lines
  const lines = text.split(/\n+/);
  return (
    <div className="rounded-xl border border-seablue-200 bg-seablue-50/60 p-4 text-sm text-seablue-900">
      {lines.map((l, i) =>
        l.trim().startsWith("- ") ? (
          <li key={i} className="ms-4 list-disc" dangerouslySetInnerHTML={{ __html: inlineMd(l.trim().slice(2)) }} />
        ) : (
          <p key={i} className={cn(i > 0 && "mt-2")} dangerouslySetInnerHTML={{ __html: inlineMd(l) }} />
        ),
      )}
    </div>
  );
}

function inlineMd(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]!).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\[(.+?)\]\((https?:[^)]+)\)/g, '<a class="underline" target="_blank" rel="noreferrer" href="$2">$1</a>');
}

/** One placed field: label, control (via the widget registry), hint and error. */
export function FieldView({ field, ctx }: { field: UiField; ctx: RendererCtx }) {
  const { document, locale, context, readOnly, onFieldChange } = ctx;
  const form = useFormContext();
  const values = form.watch();
  const node = schemaAtPath(document.schema, field.path);
  if (!node) return <p className="text-xs text-danger-600">Unknown field: {field.path}</p>;
  const vis = visibility(document.uiSchema, values);
  const parentRequired = isRequiredBySchema(document.schema, field.path);
  const required = parentRequired || vis.required.has(field.path);
  const disabled = vis.disabled.has(field.path) || node["x-ncp"]?.readOnly === true;
  const widgetKey = resolveWidgetKey(node, field);
  const widget = getWidget(widgetKey) ?? getWidget("text")!;
  const label = labelOf(node, field.path, locale);
  const hint = localize(node["x-ncp"]?.hint, locale) || descriptionOf(node, locale);
  const placeholder = localize(node["x-ncp"]?.placeholder, locale) || undefined;
  const options = widgetOptions(node, field);
  const error = getAtPath(form.formState.errors, field.path) as { message?: string } | undefined;
  const errorMessage = typeof error?.message === "string" ? error.message : undefined;
  const Widget = widget.component;
  const explain = context.onExplain ? <button type="button" className="text-xs text-aegold-700 hover:underline" onClick={() => context.onExplain?.(field.path)}>?</button> : undefined;

  if (readOnly) {
    return (
      <Field label={widget.ownLabel ? undefined : label} hint={undefined}>
        <ReadOnlyValue path={field.path} schema={node} value={getAtPath(values, field.path)} locale={locale} context={context} ui={field} />
      </Field>
    );
  }

  return (
    <Controller
      name={field.path}
      control={form.control}
      render={({ field: rhf, fieldState }) => {
        const props: WidgetProps = {
          id: `f-${field.path.replace(/[^a-zA-Z0-9_-]/g, "-")}`,
          path: field.path,
          schema: node,
          ui: field,
          value: rhf.value,
          onChange: (v) => {
            rhf.onChange(v);
            onFieldChange?.(field.path, v);
          },
          onBlur: rhf.onBlur,
          required,
          disabled,
          readOnly: false,
          invalid: fieldState.invalid,
          locale,
          context,
          options,
          label,
          placeholder,
          error: errorMessage,
          renderChild: (childPath, _childSchema, ui) => <FieldView field={{ path: childPath, ...ui }} ctx={{ ...ctx, document: { ...document, schema: document.schema } }} key={childPath} />,
        };
        // nested children resolve against the root schema: pass through when the child path is inside this node
        if (widget.ownLabel) {
          return (
            <Field hint={hint} error={errorMessage} action={explain}>
              <Widget {...props} />
            </Field>
          );
        }
        if (widgetKey === "party") {
          // party renders its own inner fields + error on the name
          return (
            <Field label={label} required={required} hint={hint} action={explain}>
              <Widget {...props} error={errorMessage ?? childError(form.formState.errors, field.path)} />
            </Field>
          );
        }
        return (
          <Field label={label} required={required} hint={hint} error={errorMessage ?? childError(form.formState.errors, field.path)} action={explain}>
            {(id) => <Widget {...props} id={id} />}
          </Field>
        );
      }}
    />
  );
}

function childError(errors: unknown, path: string): string | undefined {
  const node = getAtPath(errors, path) as Record<string, unknown> | undefined;
  if (!node || typeof node !== "object") return undefined;
  if (typeof (node as { message?: unknown }).message === "string") return (node as { message: string }).message;
  for (const v of Object.values(node)) {
    if (v && typeof v === "object") {
      const m = (v as { message?: unknown }).message;
      if (typeof m === "string") return m;
      const deeper = childError({ x: v }, "x");
      if (deeper) return deeper;
    }
  }
  return undefined;
}

function isRequiredBySchema(schema: PropertySchema, path: string): boolean {
  const segs = path.split(".");
  const leaf = segs.pop()!;
  const parent = segs.length ? schemaAtPath(schema, segs.join(".")) : schema;
  return parent?.required?.includes(leaf) ?? false;
}

function ReviewStep({ step, ctx, steps, onEdit }: { step: UiStep; ctx: RendererCtx; steps: UiStep[]; onEdit: (i: number) => void }) {
  const { locale, document, context } = ctx;
  const form = useFormContext();
  const values = form.watch();
  const declarationPath = "__declaration";
  const declared = form.watch(declarationPath) === true;
  const declErr = getAtPath(form.formState.errors, declarationPath);
  return (
    <section className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold">{localize(step.title, locale)}</h2>
        {step.description ? <p className="text-sm text-muted-foreground">{localize(step.description, locale)}</p> : null}
      </div>
      {steps.filter((s) => evaluate(s.visibleWhen, values)).map((s, i) => (
        <div key={s.key} className="rounded-2xl border border-sand-200 bg-white/60 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-semibold">{localize(s.title, locale)}</h3>
            <Button type="button" size="sm" variant="ghost" onClick={() => onEdit(i)}><Pencil /> {localize(T.edit, locale)}</Button>
          </div>
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            {s.sections.filter((sec) => evaluate(sec.visibleWhen, values)).flatMap((sec) => sec.fields.filter((f) => !f.content && evaluate(f.visibleWhen, values))).map((f) => {
              const node = schemaAtPath(document.schema, f.path);
              if (!node) return null;
              const wide = (getWidget(resolveWidgetKey(node, f))?.defaultSpan ?? 6) >= 12;
              return (
                <div key={f.path} className={cn("min-w-0", wide && "sm:col-span-2")}>
                  <dt className="text-xs uppercase tracking-wider text-sand-600">{labelOf(node, f.path, locale)}</dt>
                  <dd className="mt-0.5 text-sm"><ReadOnlyValue path={f.path} schema={node} value={getAtPath(values, f.path)} locale={locale} context={context} ui={f} /></dd>
                </div>
              );
            })}
          </dl>
        </div>
      ))}
      <label className={cn("flex items-start gap-3 rounded-xl border p-4 text-sm", declared ? "border-aegold-400 bg-aegold-50" : declErr ? "border-danger-300 bg-danger-50/40" : "border-sand-300 bg-white/70")}>
        <Controller name={declarationPath} control={form.control} render={({ field: rhf }) => <Checkbox className="mt-0.5" checked={rhf.value === true} onCheckedChange={(v) => rhf.onChange(v === true)} aria-invalid={!!declErr || undefined} />} />
        <span>{localize(step.declaration ?? T.declaration, locale)}</span>
        {declared ? <Check className="ms-auto size-4 text-success-600" /> : null}
      </label>
    </section>
  );
}
